/**
 * Sandboxes v1 view (the list page's `?view=v1`)
 *
 * Single template-first view of the org's sandbox surface. Each row is an
 * environment template with its sandboxes nested as sub-rows. When the org
 * has no templates yet, the page swaps to a full-screen Canary-CLI hero
 * with a "New Template" CTA. Once templates exist, the same hero content
 * is reachable from a help icon in the page header.
 *
 * @module features/sandboxes/components/SandboxV1View
 */

import React from 'react';
import {
  Page,
  PageHeader,
  Button,
  Drawer,
  DrawerTrigger,
  DrawerContent,
  Flex,
  LoadingOrbit,
} from '@chatsdet/ui';
import { Plus, Question } from '@phosphor-icons/react';
import { BRAND } from '../../../brand';
import {
  useEnvironmentTemplates,
  type EnvironmentTemplate,
} from '../../../hooks/useEnvironmentTemplates';
import { useEnvironmentInstances } from '../../../hooks/useEnvironmentInstances';
import { useGitHubRepositories } from '../hooks/useGitHubRepositories';
import { CreateInstanceDialog } from './CreateInstanceDialog';
import { CreateTemplateDialog } from './CreateTemplateDialog';
import { SandboxCliHero } from './SandboxCliHero';
import { TemplateRow } from './TemplateRow';

export const SandboxV1View: React.FC<{ switcher: React.ReactNode }> = ({ switcher }) => {
  const name = BRAND.name;

  const githubRepos = useGitHubRepositories();
  const templatesQuery = useEnvironmentTemplates();
  const instancesQuery = useEnvironmentInstances();

  const [createTemplateOpen, setCreateTemplateOpen] = React.useState(false);
  const [launchTemplate, setLaunchTemplate] = React.useState<EnvironmentTemplate | null>(null);
  const [helpDrawerOpen, setHelpDrawerOpen] = React.useState(false);

  const templates = templatesQuery.data?.data ?? [];
  const instances = instancesQuery.data?.data ?? [];

  // Group instances by templateId so each TemplateRow gets only its own
  // sandboxes without each row re-filtering the full list.
  const instancesByTemplateId = React.useMemo(() => {
    const map = new Map<string, typeof instances>();
    for (const instance of instances) {
      const bucket = map.get(instance.templateId) ?? [];
      bucket.push(instance);
      map.set(instance.templateId, bucket);
    }
    return map;
  }, [instances]);

  const isLoading = templatesQuery.isLoading;
  const hasTemplates = templates.length > 0;

  return (
    <Page>
      <PageHeader
        title="Test Environments"
        actions={
          <Flex gap="sm" align="center">
            {switcher}
            {hasTemplates && (
              <>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="CLI help"
                onPress={() => setHelpDrawerOpen(true)}
              >
                <Question size={18} />
              </Button>
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus size={16} />}
                onPress={() => setCreateTemplateOpen(true)}
              >
                New Template
              </Button>
              </>
            )}
          </Flex>
        }
      />

      {isLoading ? (
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      ) : !hasTemplates ? (
        <SandboxCliHero variant="empty-state" onNewTemplate={() => setCreateTemplateOpen(true)} />
      ) : (
        <Flex direction="column" gap="md">
          {templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              instances={instancesByTemplateId.get(template.id) ?? []}
              onLaunch={setLaunchTemplate}
            />
          ))}
        </Flex>
      )}

      <CreateTemplateDialog
        isOpen={createTemplateOpen}
        onOpenChange={setCreateTemplateOpen}
        githubRepositories={githubRepos.data?.repositories}
      />

      {launchTemplate && (
        <CreateInstanceDialog
          template={launchTemplate}
          isOpen={launchTemplate !== null}
          onOpenChange={(open) => {
            if (!open) setLaunchTemplate(null);
          }}
          onSuccess={() => instancesQuery.refetch()}
        />
      )}

      <DrawerTrigger isOpen={helpDrawerOpen} onOpenChange={setHelpDrawerOpen}>
        <span />
        <Drawer title={`${name} CLI`}>
          <DrawerContent>
            <SandboxCliHero variant="drawer" />
          </DrawerContent>
        </Drawer>
      </DrawerTrigger>
    </Page>
  );
};
